import { useState } from "react";
import axios from "axios";
import { API } from "@/lib/api";
import { formatApiError } from "@/lib/adminApi";

const ROOMS = ["Living room", "Dining room", "Primary Bedroom", "Home Office", "Additional room", "Other"];
const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/;
const PHONE_RE = /^[0-9+()\-.\sxX]+$/;
const EMPTY = { first_name: "", last_name: "", email: "", phone: "", project_address: "", rooms: [], other_room: "", total_rooms: "", budget: "", keep_items: "", notes: "", completion_date: "", website: "" };

const validate = (f) => {
  const e = {};
  if (!f.first_name.trim()) e.first_name = "Please enter your first name";
  if (!f.last_name.trim()) e.last_name = "Please enter your last name";
  if (!f.email.trim()) e.email = "Please enter your email address";
  else if (!EMAIL_RE.test(f.email.trim())) e.email = "Please enter a valid email address";
  const p = f.phone.trim();
  if (!p) e.phone = "Please enter your phone number";
  else if (!PHONE_RE.test(p) || p.replace(/\D/g, "").length < 7) e.phone = "Please enter a valid phone number";
  if (f.project_address.trim().length < 3) e.project_address = "Please enter the project address";
  if (f.rooms.includes("Other") && !f.other_room.trim()) e.other_room = "Please describe the other room";
  return e;
};

const Field = ({ id, label, required, helper, error, children }) => (
  <div className={`hs-field ${error ? "is-invalid" : ""}`}>
    <label htmlFor={`inq-${id}`} className="hs-field__label">{label}{required && <span aria-hidden="true">*</span>}</label>
    {children}
    {helper && <span className="hs-field__help">{helper}</span>}
    {error && <span className="hs-field__error" data-testid={`inquiry-${id.replace(/_/g, "-")}-error`}>{error}</span>}
  </div>
);

export const InquiryForm = () => {
  const [f, setF] = useState(EMPTY);
  const [errors, setErrors] = useState({});
  const [status, setStatus] = useState("idle");
  const [serverError, setServerError] = useState("");
  const [result, setResult] = useState(null);

  const set = (k) => (e) => { setF({ ...f, [k]: e.target.value }); if (errors[k]) setErrors({ ...errors, [k]: undefined }); };
  const input = (k, props = {}) => <input id={`inq-${k}`} className="hs-input" value={f[k]} onChange={set(k)} data-testid={`inquiry-${k.replace(/_/g, "-")}-input`} {...props} />;
  const area = (k) => <textarea id={`inq-${k}`} className="hs-input" rows={3} maxLength={3000} value={f[k]} onChange={set(k)} data-testid={`inquiry-${k.replace(/_/g, "-")}-input`} />;
  const toggleRoom = (r) => setF((s) => ({ ...s, rooms: s.rooms.includes(r) ? s.rooms.filter((x) => x !== r) : [...s.rooms, r], ...(r === "Other" && s.rooms.includes("Other") ? { other_room: "" } : {}) }));

  const submit = async (e) => {
    e.preventDefault();
    if (status === "sending") return;
    const v = validate(f);
    setErrors(v);
    setServerError("");
    if (Object.keys(v).length) return;
    setStatus("sending");
    try {
      const { data } = await axios.post(`${API}/inquiries`, { ...f, rooms: ROOMS.filter((r) => f.rooms.includes(r)) });
      setResult(data);
      setStatus("done");
    } catch (err) {
      setServerError(err.response ? formatApiError(err.response.data?.detail) : "Network error. Please try again.");
      setStatus("idle");
    }
  };

  if (status === "done") {
    return (
      <div className="hs-form-success hs-rise" role="status" data-testid="inquiry-success">
        <h2>Thank you</h2>
        <p>Your inquiry has been received. We will be in touch within two business days.</p>
        <p className="hs-caption">Reference {result.inquiry_id.slice(0, 8).toUpperCase()}</p>
        <button type="button" className="hs-text-link hs-text-link--dark" onClick={() => { setF(EMPTY); setStatus("idle"); }} data-testid="inquiry-send-another-button">Send another inquiry</button>
      </div>
    );
  }

  return (
    <form className="hs-form hs-fade" onSubmit={submit} noValidate data-testid="inquiry-form">
      <div className="hs-form__row">
        <Field id="first_name" label="First Name" required error={errors.first_name}>{input("first_name", { autoComplete: "given-name", maxLength: 80 })}</Field>
        <Field id="last_name" label="Last Name" required error={errors.last_name}>{input("last_name", { autoComplete: "family-name", maxLength: 80 })}</Field>
      </div>
      <div className="hs-form__row">
        <Field id="email" label="Email Address" required error={errors.email}>{input("email", { type: "email", autoComplete: "email" })}</Field>
        <Field id="phone" label="Phone Number" required error={errors.phone}>{input("phone", { type: "tel", autoComplete: "tel", maxLength: 40 })}</Field>
      </div>
      <Field id="project_address" label="Project Address (including unit number)" required error={errors.project_address}>{input("project_address", { autoComplete: "street-address", maxLength: 300 })}</Field>
      <fieldset className="hs-field" data-testid="inquiry-rooms-group">
        <legend className="hs-field__label">Which rooms would you like to decorate?</legend>
        <div className="hs-checks">
          {ROOMS.map((r) => (
            <label key={r} className="hs-check">
              <input type="checkbox" checked={f.rooms.includes(r)} onChange={() => toggleRoom(r)} data-testid={`inquiry-room-${r.toLowerCase().replace(/\s+/g, "-")}`} />
              <span>{r}</span>
            </label>
          ))}
        </div>
        <input className="hs-input hs-input--other" placeholder="Other room" aria-label="Other room" value={f.other_room} onChange={set("other_room")} disabled={!f.rooms.includes("Other")} maxLength={120} data-testid="inquiry-other-room-input" />
        {errors.other_room && <span className="hs-field__error" data-testid="inquiry-other-room-error">{errors.other_room}</span>}
      </fieldset>
      <div className="hs-form__row">
        <Field id="total_rooms" label="Total number of rooms" helper="Count each room separately, including multiple bedrooms">{input("total_rooms", { maxLength: 40, inputMode: "numeric" })}</Field>
        <Field id="budget" label="Total furniture and decor budget" helper="A range is fine. Separate from our design fee">{input("budget", { maxLength: 120 })}</Field>
      </div>
      <Field id="keep_items" label="Existing items you would like to keep" helper="Please list the items and their rooms">{area("keep_items")}</Field>
      <Field id="notes" label="Notes or special requirements" helper="Pets, children, accessibility needs, delivery restrictions, or a specific deadline">{area("notes")}</Field>
      <Field id="completion_date" label="Ideal completion date">{input("completion_date", { type: "date", min: new Date().toISOString().slice(0, 10) })}</Field>
      <div className="hs-honeypot" aria-hidden="true"><label htmlFor="inq-website">Website</label><input id="inq-website" tabIndex={-1} autoComplete="off" value={f.website} onChange={set("website")} data-testid="inquiry-honeypot-input" /></div>
      {serverError && <p className="hs-form-error" role="alert" data-testid="inquiry-submit-error">{serverError}</p>}
      <button type="submit" className="hs-btn hs-btn--submit" disabled={status === "sending"} data-testid="inquiry-submit-button">{status === "sending" ? "Sending…" : "Submit"}</button>
    </form>
  );
};
